import React, { useState, useEffect } from 'react';
import { Heart, Users, MessageSquare } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface ListingInterestButtonProps {
  listingId: string;
  onShowInterested?: () => void;
}

export function ListingInterestButton({ listingId, onShowInterested }: ListingInterestButtonProps) {
  const [isInterested, setIsInterested] = useState(false);
  const [loading, setLoading] = useState(false);
  const [interestedCount, setInterestedCount] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    checkInterestStatus();
    fetchInterestedCount();
  }, [listingId]);

  const checkInterestStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from('listing_interests')
        .select('id')
        .eq('listing_id', listingId)
        .eq('user_id', user.id)
        .limit(1);

      setIsInterested(data && data.length > 0);
    } catch (error) {
      console.error('Error checking interest status:', error);
    }
  };

  const fetchInterestedCount = async () => {
    try {
      const { count } = await supabase
        .from('listing_interests')
        .select('id', { count: 'exact' })
        .eq('listing_id', listingId);

      setInterestedCount(count || 0);
    } catch (error) {
      console.error('Error fetching interested count:', error);
    }
  };

  const toggleInterest = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        // Handle not logged in state
        return;
      }

      if (isInterested) {
        // Remove interest
        await supabase
          .from('listing_interests')
          .delete()
          .eq('listing_id', listingId)
          .eq('user_id', user.id);

        setIsInterested(false);
        setInterestedCount(prev => Math.max(0, prev - 1));
      } else {
        // Add interest
        await supabase
          .from('listing_interests')
          .insert({
            listing_id: listingId,
            user_id: user.id,
            status: 'interested'
          });

        setIsInterested(true);
        setInterestedCount(prev => prev + 1);
        setShowConfirmation(true);
      }
    } catch (error) {
      console.error('Error toggling interest:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="flex items-center gap-4">
        <button
          onClick={toggleInterest}
          disabled={loading}
          className={`flex items-center gap-2 px-4 py-2 rounded-full transition-colors ${
            isInterested
              ? 'bg-primary-100 text-primary-600 hover:bg-primary-200'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          <Heart className={`w-5 h-5 ${isInterested ? 'fill-current' : ''}`} />
          <span>{isInterested ? 'Interested' : 'Show Interest'}</span>
        </button>

        {interestedCount > 0 && (
          <button
            onClick={onShowInterested}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200"
          >
            <Users className="w-5 h-5" />
            <span>{interestedCount} interested</span>
          </button>
        )}
      </div>

      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full">
            <h3 className="text-xl font-semibold mb-4">Interest Shown!</h3>
            <p className="text-gray-600 mb-6">
              {interestedCount > 1 
                ? `${interestedCount - 1} other people are also interested in this listing. Would you like to connect with them?`
                : `You're the first person to show interest in this listing!`}
            </p>
            
            <div className="flex justify-end gap-4">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 text-gray-600 hover:text-gray-800"
              >
                Maybe Later
              </button>
              <button
                onClick={() => {
                  onShowInterested?.();
                  setShowConfirmation(false);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                <MessageSquare className="w-4 h-4" />
                <span>Connect Now</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}